import React from "react";
import { useHistory } from "react-router-dom";
import qs from "qs";
import {
  WSActions,
  WSButton,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSQueries } from "../../../../query/WSQuery";
import { OnboardingStep } from "../../../Onboarding/types";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { useQueryMeridioPlanCatalog } from "../../../../query/benefits/useQueryMeridioPlanCatalog";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { useFlowContext } from "../../../../shared/components/Flow";
import { IMeridioPlanCatalogCategory } from "../../../../services/api/benefits/meridioPlanCatalog/types";
import { EnrollmentPlanCategorySection } from "./EnrollmentPlanCategorySection";
import { EnrollMeridioFlowState } from "./types";

function getSelectedSkus(
  category: string,
  state: EnrollMeridioFlowState
): string[] {
  if (category === "VoluntaryBenefits") {
    return state.voluntaryPlanSkus ?? [];
  } else if (category === "HealthInsurance") {
    return state.healthPlanSku ? [state.healthPlanSku] : [];
  } else {
    return state.dentalVisionPlanSku ? [state.dentalVisionPlanSku] : [];
  }
}

export const StepSelectPlans: React.FC<OnboardingStep> = props => {
  const history = useHistory();
  const userId = useUserId();
  const queryMemberProfile = useQueryMemberProfile(userId);
  const queryMeridioPlanCatalog = useQueryMeridioPlanCatalog();
  const [flowState, setFlowState] = useFlowContext<EnrollMeridioFlowState>();

  const member = queryMemberProfile.data;
  const searchParams = member
    ? qs.stringify({
        name: {
          first: member.user.profile.firstName,
          last: member.user.profile.lastName
        },
        phoneNumber: member.user.phone.number?.substring(2),
        email: member.user.email,
        wingspanId: member.memberId
      })
    : "";
  const scheduleCallUrl = `https://bnfts.us/wingspan-onboard?${searchParams}`;

  const hasSelection = !!(
    flowState.healthPlanSku ||
    flowState.dentalVisionPlanSku ||
    flowState.voluntaryPlanSkus?.length
  );

  const handleSelect = (category: string, sku: string) => {
    if (category === "VoluntaryBenefits") {
      setFlowState(prev => {
        const current = prev.voluntaryPlanSkus ?? [];
        const next = current.includes(sku)
          ? current.filter(s => s !== sku)
          : [...current, sku];
        return { ...prev, voluntaryPlanSkus: next };
      });
    } else if (category === "HealthInsurance") {
      setFlowState(prev => ({
        ...prev,
        healthPlanSku: prev.healthPlanSku === sku ? undefined : sku
      }));
    } else {
      setFlowState(prev => ({
        ...prev,
        dentalVisionPlanSku: prev.dentalVisionPlanSku === sku ? undefined : sku
      }));
    }
  };

  return (
    <WSQueries queries={{ queryMeridioPlanCatalog }}>
      {({ queryMeridioPlanCatalogData }) => (
        <>
          <WSText.Heading3 weight="medium" mb="S">
            Select plans
          </WSText.Heading3>
          <WSText.ParagraphSm weight="book" color="gray500" mb="L">
            Select at least one plan to get started. You can add dependents on
            the next step.{" "}
            <b>
              Reminder: Scheduling a call with Meridio is recommended to learn
              more about the plans and get started.
            </b>
          </WSText.ParagraphSm>
          <WSButton.Link
            rightIcon="open-tab"
            mb="XL"
            onClick={() => openInNewTab(scheduleCallUrl)}
          >
            Schedule call
          </WSButton.Link>

          <WSList gap="XL">
            {queryMeridioPlanCatalogData.categories.map(
              (category: IMeridioPlanCatalogCategory) => (
                <EnrollmentPlanCategorySection
                  key={category.category}
                  category={category}
                  multiSelect={category.category === "VoluntaryBenefits"}
                  selectedSkus={getSelectedSkus(category.category, flowState)}
                  onSelect={sku => handleSelect(category.category, sku)}
                />
              )
            )}
          </WSList>

          <WSActions
            mt="XL"
            alignment="fill"
            buttons={[
              { kind: "Tertiary", label: "Back", onClick: props.onBack },
              {
                label: "Continue",
                onClick: props.onNext,
                disabled: !hasSelection
              }
            ]}
          />
        </>
      )}
    </WSQueries>
  );
};
